import { Component, DestroyRef, forwardRef, HostBinding, inject, Input, OnInit, signal } from '@angular/core';
import { LorenzFormControl } from '../../lorenz-form-control';
import { LorenzFormComponentDisplayOptions } from '../../interfaces/lorenz-form-component-options.interface';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { getLorenzFormLayoutEnumString } from '../../enums/lorenz-form-layout.enum';
import { LorenzFormGroup } from '../../lorenz-form-group';
import { CommonModule } from '@angular/common';
import { LibFormValidityDotComponent } from '../../components/validity-dot/lib-form-validity-dot.component';
import { MatIconButton } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'lib-form-control-image-paste',
	imports: [ReactiveFormsModule, LibFormValidityDotComponent, CommonModule, LibFormValidityDotComponent, MatIconModule, MatIconButton, TranslatePipe],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => LibFormImagePasteComponent),
			multi: true
		}
	],
	templateUrl: './lib-form-image-paste.component.html',
	styleUrl: './lib-form-image-paste.component.scss'
})
export class LibFormImagePasteComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);

	@Input({ required: true, alias: 'control' }) lorenzFormControl!: LorenzFormControl;

	@Input({ required: false, alias: 'options' }) lorenzFormComponentDisplayOptions: LorenzFormComponentDisplayOptions = {};

	@HostBinding('attr.class') hostCssClass = '';

	lorenzFormGroup!: LorenzFormGroup;

	preview = signal<string | null>(null);
	deleteDisabled = signal(false);
	isFocused = signal(false);
	isEnabled = signal(true);
	hasLockedPreview = signal(false);

	ngOnInit() {
		this.lorenzFormGroup = this.lorenzFormControl.parent as LorenzFormGroup;

		this.lorenzFormControl.payload.displayOptions = this.lorenzFormComponentDisplayOptions;

		// prettier-ignore
		this.hostCssClass = [
				this.lorenzFormControl.getKey(),
				getLorenzFormLayoutEnumString(this.lorenzFormComponentDisplayOptions?.layout ?? null)
			].join(' ');

		this.preview.set(this.lorenzFormControl?.value ?? null);

		// prettier-ignore
		this.lorenzFormControl.valueChanges
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(v => {
					this.preview.set(v ?? null)
				});

		// prettier-ignore
		this.lorenzFormControl.statusChanges
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => this.deleteDisabled.set(this.lorenzFormControl.disabled));

		// prettier-ignore
		this.lorenzFormControl.payloadChanges
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => {
					this.isEnabled.set(!this.lorenzFormControl.isLocked());
					this.hasLockedPreview.set(this.lorenzFormControl.isLocked() && (this.preview() !== null))
				});
	}

	get deleteButtonDisabled(): boolean {
		return !this.lorenzFormControl.value;
	}

	onFocus() {
		this.isFocused.set(true);
	}
	onBlur() {
		this.isFocused.set(false);
	}

	onPaste(clipboardEvent: ClipboardEvent) {
		const clipboardItems = clipboardEvent.clipboardData?.items;

		if (!clipboardItems || clipboardItems.length === 0) {
			return;
		}

		for (let i = 0; i < clipboardItems.length; i++) {
			const clipboardItem = clipboardItems[i];
			if (clipboardItem.kind === 'file' && clipboardItem.type.startsWith('image/')) {
				const file = clipboardItem.getAsFile();
				if (file) {
					this.readFile(file);
					clipboardEvent.preventDefault();
					this.touch();
					return;
				}
			}
		}
	}

	clear(event: Event) {
		event.stopPropagation();
		// if (this.deleteDisabled()) return;
		this.lorenzFormControl.setValue(null);
		this.lorenzFormControl.markAsDirty();
		this.touch();
	}

	private readFile(file: File) {
		const fileReader = new FileReader();
		fileReader.onload = () => {
			const dataUrl = typeof fileReader.result === 'string' ? fileReader.result : null;
			this.lorenzFormControl.setValue(dataUrl);
			this.lorenzFormControl.markAsDirty();
		};
		fileReader.readAsDataURL(file);
	}

	private touch() {
		this.lorenzFormControl.markAsTouched();
		this.lorenzFormControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
	}
}
